import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PageHeader } from "../components/shell/PageHeader";
import { Button } from "../components/ui/Button";
import { EmptyState, ErrorNotice, Notice, SkeletonRows } from "../components/ui/Feedback";
import { Freshness } from "../components/ui/Freshness";
import { SearchField, SelectField } from "../components/ui/Input";
import { Pill } from "../components/ui/Status";
import type { Column } from "../components/ui/Table";
import { Cell, Pagination, TableFrame, TableHead, TableRow } from "../components/ui/Table";
import { Metric, MicroLabel } from "../components/ui/Text";
import { getPage, post } from "../lib/api";
import { customerSpend, customerStatusTone } from "../lib/customer";
import { count, since, timestamp } from "../lib/format";
import { useSession } from "../lib/session";
import type { Customer } from "../lib/types";

/**
 * Customer accounts, as the operator reads them. pay-ui spec §20.7.
 *
 * ## What this page is for
 *
 * A driver who cannot sign in, or who says a charge is missing from their list,
 * is a support call. Without this page the answer lives in `psql` — which is how
 * the two orders currently owed a refund on this deployment were found, and it is
 * why the route behind this table was written at all.
 *
 * ## What it deliberately does not show
 *
 * **A balance.** §1.0.1 — this system stores no value. "Spent" here is a sum over
 * settled orders computed per request, and it is captioned as a total rather than
 * as an amount held, because the first thing a screen like this invites is
 * somebody asking where the top-up button is.
 *
 * **The Google subject.** The server does not send it; it is the account key, and
 * an operator has no use for it.
 *
 * ## The only action is disabling
 *
 * No create — an account is a verified Google identity, and one an operator typed
 * would be a login nobody can use. No edit — the email and name are Google's, and
 * refreshed on every sign-in, so an edit here would be silently overwritten. No
 * delete — orders reference the account, and while the ledger row would survive,
 * a deletion would strip the ownership from every receipt the driver can see.
 */

const COLUMNS: readonly Column[] = [
  { key: "email", label: "Account", width: "26%" },
  { key: "status", label: "Status", width: "10%" },
  { key: "charges", label: "Charges", numeric: true, width: "9%" },
  { key: "spent", label: "Spent", numeric: true, width: "12%" },
  { key: "last", label: "Last charge", width: "16%" },
  { key: "signedIn", label: "Signed in", width: "13%" },
  { key: "devices", label: "Devices", numeric: true, width: "9%" },
];

/**
 * Slower than the public-orders table. Nothing on this page is money in flight —
 * an account changes when somebody signs in, which is not a thing an operator
 * watches happen.
 */
const POLL_MS = 30_000;

/** Timestamps here are not station-local: an account belongs to no depot. */
const UTC = "UTC";

export function Customers() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useSession();
  const [params, setParams] = useSearchParams();
  const [notice, setNotice] = useState<string | undefined>(undefined);

  const search = params.get("search") ?? "";
  const status = params.get("status") ?? "";
  const page = positiveInt(params.get("page")) ?? 1;

  const customers = useQuery({
    queryKey: ["customers", { search, status, page }],
    queryFn: () => getPage<Customer>("/customers", { query: { search, status, page } }),
    refetchInterval: POLL_MS,
    placeholderData: (previous) => previous,
  });

  const rows = customers.data?.rows ?? [];
  /**
   * The action column appears if either half is held; each row's button is then
   * the one direction that row can go, shown only if that direction is held.
   */
  const canDisable = can("customers.disable");
  const canEnable = can("customers.enable");
  const canAct = canDisable || canEnable;
  const now = Date.now();

  const setStatus = useMutation({
    mutationFn: ({ id, next }: { id: string; next: "disable" | "enable" }) =>
      post<Customer>(`/customers/${id}/${next}`, {}),
    onSuccess: (customer) => {
      setNotice(
        customer.status === "disabled"
          ? `${customer.email} disabled. Every device on the account has been signed out.`
          : `${customer.email} enabled.`,
      );
      void queryClient.invalidateQueries({ queryKey: ["customers"] });
    },
  });

  function setFilter(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value === "") next.delete(key);
    else next.set(key, value);
    next.delete("page");
    setParams(next, { replace: true });
  }

  return (
    <>
      <PageHeader
        eyebrow="Operations"
        title="Customers"
        metrics={
          <>
            <Metric
              label="Matching"
              value={count(customers.data?.meta?.total)}
              hint="these filters"
            />
            <Metric
              label="Disabled"
              value={count(rows.filter((row) => row.status === "disabled").length)}
              tone={rows.some((row) => row.status === "disabled") ? "signal" : "muted"}
              hint="on this page"
            />
          </>
        }
        freshness={
          <Freshness
            updatedAt={customers.dataUpdatedAt}
            isFetching={customers.isFetching}
            intervalMs={POLL_MS}
          />
        }
        lede="An account is an index onto charges somebody has already paid for — it holds no balance and authorises nothing. Signing in is how a driver keeps their receipts across a lost cookie or a new phone."
      />

      <form
        onSubmit={(event) => event.preventDefault()}
        className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2"
      >
        <SearchField
          label="Email"
          placeholder="Any part of the address"
          value={search}
          onChange={(event) => setFilter("search", event.target.value)}
          hint="Matches anywhere in the address, not just the start."
        />
        <SelectField
          label="Status"
          value={status}
          onChange={(event) => setFilter("status", event.target.value)}
        >
          {/* The two values the CHECK constraint permits. The server refuses an
              unknown one rather than ignoring it, so a filter that can never match
              is a 400 rather than a silent full table. */}
          <option value="">Any</option>
          <option value="active">Active</option>
          <option value="disabled">Disabled</option>
        </SelectField>
      </form>

      {notice === undefined ? null : <Notice tone="ok">{notice}</Notice>}
      {customers.isError ? (
        <ErrorNotice error={customers.error} onRetry={() => void customers.refetch()} />
      ) : null}
      {setStatus.isError ? <ErrorNotice error={setStatus.error} /> : null}

      <div className="flex flex-col gap-3">
        <TableFrame>
          <TableHead columns={canAct ? [...COLUMNS, ACTION_COLUMN] : COLUMNS} />
          {customers.isPending ? (
            <SkeletonRows rows={8} columns={COLUMNS.length + (canAct ? 1 : 0)} />
          ) : (
            <tbody>
              {rows.map((row, index) => {
                const disabled = row.status === "disabled";

                return (
                  <TableRow
                    key={row.id}
                    index={index}
                    // The account's charges, on the table that already knows how
                    // to render an order — rather than a second, thinner copy of
                    // it on a detail page nobody would keep in step.
                    onActivate={() => navigate(`/public-orders?customerId=${row.id}`)}
                    isFaulted={disabled}
                  >
                    <Cell>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-ink">{row.email}</span>
                        <span className="text-micro tracking-normal normal-case text-ink-faint">
                          {row.displayName ?? "no name from Google"}
                        </span>
                      </div>
                    </Cell>
                    <Cell>
                      <Pill tone={customerStatusTone(row.status)}>{row.status}</Pill>
                    </Cell>
                    <Cell numeric muted={row.orderCount === 0}>
                      {row.orderCount === 0 ? "—" : count(row.orderCount)}
                    </Cell>
                    <Cell numeric muted={row.settledCount === 0}>
                      {customerSpend(row)}
                    </Cell>
                    <Cell muted={row.lastOrderAt === null}>
                      {row.lastOrderAt === null ? "never" : since(row.lastOrderAt, now)}
                    </Cell>
                    <Cell muted={row.lastLoginAt === null}>
                      {row.lastLoginAt === null ? "never" : timestamp(row.lastLoginAt, UTC)}
                    </Cell>
                    <Cell numeric muted={row.liveSessions === 0}>
                      {row.liveSessions === 0 ? "—" : count(row.liveSessions)}
                    </Cell>
                    {canAct ? (
                      <Cell>
                        {(disabled ? canEnable : canDisable) ? (
                          <Button
                            variant={disabled ? "secondary" : "danger"}
                            pending={setStatus.isPending && setStatus.variables?.id === row.id}
                            onClick={(event) => {
                              // The row itself navigates; a button inside one must
                              // not do both.
                              event.stopPropagation();
                              setStatus.mutate({
                                id: row.id,
                                next: disabled ? "enable" : "disable",
                              });
                            }}
                          >
                            {disabled ? "Enable" : "Disable"}
                          </Button>
                        ) : null}
                      </Cell>
                    ) : null}
                  </TableRow>
                );
              })}
            </tbody>
          )}
        </TableFrame>

        {!customers.isPending && rows.length === 0 ? (
          <EmptyState
            title="No customer accounts match these filters."
            detail="An account appears the first time somebody signs in with Google from the payment app. Most drivers never do — paying at a pump needs no account."
          />
        ) : null}

        {rows.some((row) => row.status === "disabled") ? (
          <MicroLabel>
            A marked row cannot sign in. Their past charges are unaffected, and any refund owed is
            still owed.
          </MicroLabel>
        ) : null}

        <Pagination
          meta={customers.data?.meta}
          shown={rows.length}
          onPage={(next) => {
            const nextParams = new URLSearchParams(params);
            if (next <= 1) nextParams.delete("page");
            else nextParams.set("page", String(next));
            setParams(nextParams);
          }}
        />
      </div>
    </>
  );
}

const ACTION_COLUMN: Column = { key: "act", label: "", width: "10%" };

function positiveInt(raw: string | null): number | undefined {
  if (raw === null || !/^\d+$/.test(raw)) return undefined;
  const value = Number(raw);
  return value >= 1 ? value : undefined;
}
